/* eslint-disable @typescript-eslint/naming-convention */
import { Grid, Typography, Theme, BoxProps } from '@quillbot/design-system';
import clsx from 'clsx';
import { GenericObject } from 'core/models/platform';
import { useRouter } from 'next/router';
import React, { memo, useCallback } from 'react';

import {
  breakWord,
  column,
  createBorder,
  createBorderRadius,
  cursorPointer,
  displayFlex,
  displayNone,
  fontSize,
  fontWeight,
  grow,
  justifyStart,
  overflowHidden,
  pointerEventsNone,
  primaryColor,
  row,
  truncate,
  unitPX,
  UNITS,
} from 'src/researcher/styles';
import { useKebab } from './Card.provider';

export const card_root_class = 'card_root';
export const card_controls_root_class = 'card_controls_root';
export const preview_text_class = 'preview_text';
export const show_control_on_hover_class = 'show_control_on_hover';
export const show_control_on_mount_class = 'show_control_on_mount';
export const card_date_container_class = 'card_date_container';

export const controlButtonStyles = { p: unitPX(UNITS[0]) };

export const loaderStyles = {
  width: unitPX(UNITS['3pt']),
  height: unitPX(UNITS['3pt']),
};

const CARD_SIZE = {
  WIDTH: UNITS['26pt'],
  HEIGHT: UNITS['31pt'],
};

const linesDefault = '9';
const lineOnControlSectionOpen = '8';

const lineClamp = {
  display: '-webkit-box',
  boxOrient: 'vertical',
  '-webkit-box-orient': 'vertical',
  lineClamp: linesDefault,
  '-webkit-line-clamp': linesDefault,
};

const textClamped = {
  lineClamp: lineOnControlSectionOpen,
  '-webkit-line-clamp': lineOnControlSectionOpen,
};

const card_control_border = createBorder(['top']);
const card_border = createBorder(['top', 'right', 'bottom', 'left']);
const card_border_primary = createBorder(['top', 'right', 'bottom', 'left'], primaryColor);

const getSizeCSS = ({ WIDTH, HEIGHT }: { WIDTH: number; HEIGHT: number }) => ({
  minHeight: unitPX(HEIGHT),
  maxHeight: unitPX(HEIGHT),
  minWidth: unitPX(WIDTH),
  maxWidth: unitPX(WIDTH),
});

const styles = {
  cardSectionSpacing: { mt: unitPX(UNITS.pt) },

  cardShadow: {
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
  },

  cardBorder: card_border,

  cardHoverBorder: {
    boxSizing: 'border-box',
    '&:hover': card_border_primary,
  },

  cardBorderRadius: createBorderRadius(
    ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
    unitPX(UNITS['1.5pt'])
  ),
  // shows controls on hover of the card
  showControlsOnHover: {
    boxSizing: 'border-box',
    // shows controls on hover and hide when hover out
    [`& .${card_controls_root_class}`]: displayNone,
    [`&:hover .${card_controls_root_class}`]: displayFlex,
  },
  // shows controls always
  showControlsOnMount: {
    boxSizing: 'border-box',
    [`& .${card_controls_root_class}`]: displayFlex,
  },

  previewText: {
    [`& .${preview_text_class}`]: lineClamp,
  },
  previewTextClampOnHover: {
    [`&:hover .${preview_text_class}`]: textClamped,
  },
  previewTextClampOnMount: {
    [`& .${preview_text_class}`]: textClamped,
  },

  controlSectionBorder: card_control_border,

  controlSection: {
    pt: unitPX(UNITS.pt),
  },

  rootGridCard: {
    [`& .${card_date_container_class}`]: displayFlex,
    [`&:hover .${card_date_container_class}`]: displayNone,
    [`&.${show_control_on_mount_class} .${card_date_container_class}`]: displayNone,
  },
};

export type CardLayoutOwnProps = {
  controls?: 'hover' | 'card-mount';
  onCardClick?: Function;
  WIDTH?: number;
  HEIGHT?: number;
  padding?: any;
  modifierClassName?: string;
  sx?: BoxProps['sx'];
};

export function CardLayout({
  controls = 'hover',
  onCardClick,
  WIDTH = CARD_SIZE.WIDTH,
  HEIGHT = CARD_SIZE.HEIGHT,
  padding = UNITS['2pt'],
  modifierClassName,
  sx = null,
  ...props
}: React.PropsWithChildren<CardLayoutOwnProps>) {
  const [anchorEl] = useKebab();
  const isKebabOpen = Boolean(anchorEl);

  const classes = [];
  const internalSX: any[] = [styles.previewTextClampOnHover];

  if (controls === 'card-mount') {
    classes.push(show_control_on_mount_class);
    internalSX.push(styles.showControlsOnMount, styles.previewTextClampOnMount);
  } else {
    classes.push(show_control_on_hover_class);
    internalSX.push(
      styles.showControlsOnHover,
      // preview text clamped always
      styles.previewTextClampOnMount
      // styles.previewTextClampOnHover
    );
  }

  if (isKebabOpen) {
    classes.push(show_control_on_mount_class);
    internalSX.push(styles.showControlsOnMount);
  }

  const internalProps: GenericObject = {};

  if (onCardClick) {
    internalProps.onClick = onCardClick;
  }

  if (sx) {
    if (Array.isArray(sx)) {
      internalSX.push(...sx);
    } else {
      internalSX.push(sx);
    }
  }

  return (
    <Grid item>
      <Grid
        {...props}
        {...internalProps}
        className={clsx(card_root_class, modifierClassName, ...classes)}
        container
        sx={[
          onCardClick && cursorPointer,
          getSizeCSS({ WIDTH, HEIGHT }),
          styles.cardShadow,
          styles.cardBorder,
          styles.cardHoverBorder,
          styles.cardBorderRadius,
          { p: unitPX(padding) },
          styles.previewText,
          styles.rootGridCard,
          ...internalSX,
        ]}
      >
        {props.children}
      </Grid>
    </Grid>
  );
}

export type CardInfoOwnProps = {
  time: string;
  title: string;
  preview: string;
  modifierClassName?: string;
  sx?: BoxProps['sx'];
};

export function CardInfo({
  title,
  preview,
  children,
  modifierClassName,
  time,
  sx,
}: React.PropsWithChildren<CardInfoOwnProps>) {
  return (
    <Grid
      flex={1}
      container
      direction="column"
      alignItems="space-between"
      sx={sx}
      className={modifierClassName}
    >
      {/* <Grid item>
        <Typography
          component="span"
          sx={[
            fontSize[12],
            fontWeight[400],
            { color: (theme: Theme) => theme.palette.text.secondary },
          ]}
        >
          {time}
        </Typography>
      </Grid> */}
      <Grid item>
        <Typography
          component="span"
          sx={[fontSize[16], fontWeight[600], truncate, { whiteSpace: 'nowrap' }]}
        >
          {title}
        </Typography>
      </Grid>
      <Grid item flex={1} sx={[styles.cardSectionSpacing, overflowHidden]}>
        <Typography
          className={preview_text_class}
          sx={[
            fontSize[12],
            fontWeight[400],
            breakWord,
            truncate,
            overflowHidden,
            { color: (theme: Theme) => theme.palette.text.secondary },
          ]}
        >
          {preview}
        </Typography>
      </Grid>
      <Grid
        item
        sx={[styles.controlSection, styles.cardSectionSpacing]}
        className={card_date_container_class}
      >
        <Typography
          className={preview_text_class}
          sx={[
            fontSize[12],
            fontWeight[400],

            { color: (theme: Theme) => theme.palette.text.secondary },
          ]}
        >
          {time}
        </Typography>
      </Grid>
      <Grid
        item
        className={card_controls_root_class}
        sx={[styles.controlSection, styles.controlSectionBorder, styles.cardSectionSpacing]}
      >
        {children}
      </Grid>
    </Grid>
  );
}
